import axiosInstance from '@/lib/axios';

export interface RevenueItem {
  id: string;
  invoiceNumber: string;
  clientName?: string;
  customer?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  date?: string;
  invoiceDate?: string;
  dueDate?: string;
  status?: 'paid' | 'pending' | 'overdue';
  currency: string;
  currencySymbol?: string;
  amount?: number;
  subtotal?: number;
  totalTax?: number;
  totalAmount?: number;
  category?: string;
  description?: string;
  items?: Array<{
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface RevenueFilters {
  search?: string;
  status?: string;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface RevenueResponse {
  data: RevenueItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

class RevenueService {
  async getRevenue(filters: RevenueFilters = {}): Promise<RevenueResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const url = `/revenue?${params.toString()}`;
      
      const response = await axiosInstance.get(url);
      
      let revenueData: RevenueItem[] = [];
      let total = 0;
      let page = 1;
      let limit = 20;
      let hasMore = false;

      if (response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          revenueData = response.data.data;
          total = response.data.pagination?.total || response.data.total || response.data.data.length;
          page = response.data.pagination?.page || response.data.page || 1;
          limit = response.data.pagination?.limit || response.data.limit || 20;
          hasMore = response.data.pagination?.hasNextPage || response.data.hasMore || false;
        } else if (Array.isArray(response.data.data)) {
          revenueData = response.data.data;
          total = response.data.total || response.data.data.length;
          page = response.data.page || 1;
          limit = response.data.limit || response.data.data.length;
          hasMore = response.data.hasMore || false;
        } else if (Array.isArray(response.data)) {
          revenueData = response.data;
          total = response.data.length;
          page = 1;
          limit = response.data.length;
          hasMore = false;
        }
      }

      return {
        data: revenueData,
        total,
        page,
        limit,
        hasMore,
      };
    } catch (error) {
      console.error('Error fetching revenue:', error);
      throw error;
    }
  }

  async getRevenueStats(filters: RevenueFilters = {}): Promise<{
    paid: number;
    pending: number;
    overdue: number;
  }> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const url = `/revenue/stats?${params.toString()}`;
      const response = await axiosInstance.get(url);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching revenue stats:', error);
      throw error;
    }
  }
}

export const revenueService = new RevenueService();

